import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import gibletLogo from '@/assets/giblet-logo.png';

const functionalAreas = [
  { id: 'construction-automation', name: 'Construction Automation', description: 'Robotics, autonomous equipment, and automated construction processes' },
  { id: 'onsite-automation', name: 'On-site Automation', description: 'Field-deployed automation systems and real-time site monitoring' },
  { id: 'construction-methods', name: 'Construction Methods', description: 'Traditional and innovative construction techniques and methodologies' },
  { id: 'cost-management', name: 'Cost Management', description: 'Budget planning, cost estimation, and financial controls' },
  { id: 'data-analytics', name: 'Data Analytics', description: 'Construction data collection, analysis, and predictive modeling' },
  { id: 'engineering-design', name: 'Engineering & Design', description: 'Design standards, BIM, and engineering best practices' },
  { id: 'health-safety', name: 'Health & Safety', description: 'OSHA compliance, safety protocols, and workforce protection' },
  { id: 'emerging-technologies', name: 'Emerging Technologies', description: 'AI, IoT, digital twins, and next-generation construction tech' },
  { id: 'supply-chain', name: 'Supply Chain', description: 'Materials procurement, logistics, and vendor management' },
  { id: 'project-planning', name: 'Project Planning', description: 'Scheduling, resource allocation, and project controls' },
  { id: 'quality-assurance', name: 'Quality Assurance', description: 'Inspection standards, quality metrics, and compliance verification' },
  { id: 'stakeholder-communication', name: 'Stakeholder Communication', description: 'Reporting, collaboration tools, and stakeholder engagement' },
  { id: 'environmental-impact', name: 'Environmental Impact', description: 'Environmental assessments, sustainability metrics, and compliance' },
  { id: 'green-building', name: 'Green Building', description: 'LEED certification, sustainable materials, and energy efficiency' },
  { id: 'workforce-development', name: 'Workforce Development', description: 'Training programs, skills development, and labor management' },
];

export default function DemoCallForKnowledgeFunctionalArea() {
  const navigate = useNavigate();
  const [selectedArea, setSelectedArea] = useState('');

  const handleNext = () => {
    if (selectedArea) {
      const area = functionalAreas.find(a => a.id === selectedArea);
      navigate('/demo-call-for-knowledge/connection', {
        state: { functionalArea: selectedArea, functionalAreaName: area?.name }
      });
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center p-6">
      <div className="max-w-3xl w-full space-y-8">
        <div className="flex justify-center mb-8">
          <img src={gibletLogo} alt="Giblet Logo" className="h-16 w-auto" />
        </div>

        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">Select Functional Area</h1>
          <p className="text-lg text-muted-foreground">
            Choose the CURT domain where knowledge contributions are needed
          </p>
        </div>

        <div className="bg-card rounded-lg p-8 shadow-lg space-y-2 max-h-[500px] overflow-y-auto">
          <RadioGroup value={selectedArea} onValueChange={setSelectedArea}>
            {functionalAreas.map((area) => (
              <div key={area.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-accent/50 transition-colors">
                <RadioGroupItem value={area.id} id={area.id} className="mt-1" />
                <Label htmlFor={area.id} className="flex-1 cursor-pointer">
                  <div className="font-semibold text-foreground">{area.name}</div>
                  <div className="text-sm text-muted-foreground mt-0.5">{area.description}</div>
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        <div className="flex justify-center gap-4">
          <Button variant="outline" onClick={() => navigate('/demo-call-for-knowledge')} className="min-w-[120px]">
            Back
          </Button>
          <Button onClick={handleNext} disabled={!selectedArea} className="min-w-[120px]">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
